import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiArrowLeft,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { taxService } from '../../services/apiService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const CYCLES = [
  { value: 'monthly', label: 'Monthly', desc: 'File once per month' },
  { value: 'quarterly', label: 'Quarterly', desc: 'File once per quarter' },
  { value: 'yearly', label: 'Yearly', desc: 'File once per year' },
];

const TaxFiling = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // If navigated from TaxSummary, we get the pre-filled summary
  const prefilled = location.state || {};

  const [cycle, setCycle] = useState(prefilled.cycle || 'monthly');
  const [notes, setNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const createMutation = useMutation(taxService.createFiling, {
    onSuccess: (response) => {
      toast.success('Filing draft created');
      queryClient.invalidateQueries('tax-filings');
      queryClient.invalidateQueries('tax-summary');
      navigate(`/tax/filing/${response.data.filing_id}`);
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to create filing';
      toast.error(msg);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!acknowledged) {
      toast.error('Please acknowledge the declaration before creating');
      return;
    }

    createMutation.mutate({
      cycle,
      notes: notes.trim() || null,
    });
  };

  // Preview of what's shown if we came from TaxSummary
  const preview = prefilled.summary;
  const hasPreview =
    preview && (preview.total_transactions || 0) > 0;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            className="icon-btn"
            onClick={() => navigate('/tax/filing')}
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">Create Tax Filing</h1>
            <p className="page-subtitle">
              Prepare a GST filing for a specific period
            </p>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '24px',
          alignItems: 'flex-start',
        }}
      >
        {/* Main form */}
        <div className="card">
          <form onSubmit={handleSubmit}>
            {/* Cycle selector */}
            <div className="form-group">
              <label className="form-label">Filing Cycle *</label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                  marginTop: '8px',
                }}
              >
                {CYCLES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCycle(c.value)}
                    style={{
                      padding: '16px',
                      border:
                        cycle === c.value
                          ? '2px solid #1a73e8'
                          : '1px solid #d1d5db',
                      background:
                        cycle === c.value ? '#e8f0fe' : '#ffffff',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color:
                          cycle === c.value ? '#1a73e8' : '#1f2937',
                      }}
                    >
                      {c.label}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: '4px',
                      }}
                    >
                      {c.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview of the summary (if available) */}
            {hasPreview && (
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                  }}
                >
                  <FiInfo size={16} color="#1a73e8" />
                  <span style={{ fontSize: '13px', color: '#1f2937' }}>
                    Preview from Tax Summary — the actual numbers will be
                    computed on submit for the selected period
                  </span>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '8px',
                    fontSize: '13px',
                  }}
                >
                  <PreviewRow
                    label="Period"
                    value={`${formatDate(preview.period_start)} — ${formatDate(preview.period_end)}`}
                  />
                  <PreviewRow
                    label="Gross Sales"
                    value={formatCurrency(preview.gross_sales || 0)}
                  />
                  <PreviewRow
                    label="Net Sales"
                    value={formatCurrency(preview.net_sales || 0)}
                  />
                  <PreviewRow
                    label="Tax Due"
                    value={formatCurrency(preview.tax_due || 0)}
                    bold
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea
                className="form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any remarks to include with this filing..."
                rows={4}
              />
            </div>

            {/* Declaration */}
            <div
              style={{
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <FiAlertCircle
                  size={20}
                  color="#d97706"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      color: '#92400e',
                      marginBottom: '4px',
                    }}
                  >
                    Declaration
                  </div>
                  <div
                    style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.5 }}
                  >
                    By creating this filing, you declare that the information
                    is accurate and complete to the best of your knowledge.
                    This filing will be recorded in the system and can be
                    submitted to the LRA for review.
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '12px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#78350f',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                    I acknowledge and confirm this declaration
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/tax/filing')}
                disabled={createMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isLoading || !acknowledged}
              >
                <FiFileText size={18} />
                {createMutation.isLoading
                  ? 'Creating...'
                  : 'Create Filing Draft'}
              </button>
            </div>
          </form>
        </div>

        {/* Info sidebar */}
        <div>
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">How it works</h2>
            </div>
            <Step number={1} title="Create Draft">
              Choose a period and create a filing draft. Numbers are computed
              from your captured sales.
            </Step>
            <Step number={2} title="Review & Submit">
              Review the numbers. Once submitted, the filing is locked and
              sent for LRA review.
            </Step>
            <Step number={3} title="Get Approved">
              The LRA reviews the filing. Approved filings become part of
              your compliance record.
            </Step>
          </div>

          <div className="card" style={{ marginTop: '16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#166534',
                marginBottom: '8px',
              }}
            >
              <FiCheckCircle size={18} />
              <strong>Data-Driven</strong>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
              All figures come directly from your POS sales. No manual entry
              required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Helper components ----------

const PreviewRow = ({ label, value, bold }) => (
  <div>
    <div style={{ color: '#6b7280', fontSize: '12px' }}>{label}</div>
    <div
      style={{
        fontWeight: bold ? 700 : 500,
        color: bold ? '#1a73e8' : '#1f2937',
      }}
    >
      {value}
    </div>
  </div>
);

const Step = ({ number, title, children }) => (
  <div
    style={{
      display: 'flex',
      gap: '12px',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6',
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: '#e8f0fe',
        color: '#1a73e8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '13px',
        flexShrink: 0,
      }}
    >
      {number}
    </div>
    <div>
      <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  </div>
);

export default TaxFiling;
